// Overrides (spec/dashboard.md section 8.3): cards over the non-A/B SmartObjects.OverrideInfo documents; each group a
// section with its condition (no ABTestCondition offered), priority, comment and rows document → parameter → new value,
// the row's component template following the parameter type; the effective-value preview with conflict detection.
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import type { Document, Json, Parameter } from '../../api/types';
import { CardList, type CardContext } from '../../content/cards/CardList';
import { ConditionOptionsContext, ConditionSummary } from '../../content/conditions/ConditionBuilder';
import { useContent } from '../../content/ContentContext';
import { formatValue } from '../../content/format';
import { useDocumentList } from '../../content/useDocumentList';
import { AB_CONDITION, OVERRIDE_INFO, effectivePatches, resolveDocument, groupsOf, overridableLeaves, paramAt, patchOf, toGroupJson, valueAt, type Effective, type OverridableLeaf, type OverrideGroupData, type PatchRow } from '../../content/overrides/model';
import { ParameterPickDialog } from '../../content/overrides/ParameterPickDialog';
import { stageRow } from '../../content/overrides/create';
import { FieldWidget } from '../../content/widgets/FieldWidget';
import { ReferencePicker } from '../../content/widgets/ReferenceField';
import { createDocumentDraft, useWidgetEnv } from '../../content/widgets/context';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button, IconButton } from '../../ui/Button';
import { EmptyState } from '../../ui/Banner';
import { Badge } from '../../ui/Chip';
import { ChevronDown, ChevronLeft, Filter, Layers, Plus, SlidersHorizontal, Trash2, X } from '../../ui/icons';
import { CommitTextInput, NumberInput } from '../../ui/inputs';
import { Popover } from '../../ui/Popover';
import { baseOf } from '../../content/actions';
import { useWorkspaceVersion } from '../../workspace/react';
import { CommonTail, NameText } from './cards';
import './liveops.css';

const NOT_AB = [{ field: 'data.flag', op: 'ne' as const, value: '1' }];

export function OverridesPage() {
  const family = useDocumentList({ template: OVERRIDE_INFO, includeHeirs: false, rules: NOT_AB, pageSize: 200 });
  const exclude = useMemo(() => [AB_CONDITION], []);
  return (
    <ConditionOptionsContext.Provider value={{ exclude }}>
      <CardList
        id="overrides"
        title="Overrides"
        template={OVERRIDE_INFO}
        includeHeirs={false}
        fixedRules={NOT_AB}
        header={(ctx) => <OverrideHeader ctx={ctx} />}
        body={(ctx) => <OverrideBody ctx={ctx} family={family.rows} />}
        emptyText="No overrides yet. An override patches values of documents for the players who pass its groups' conditions; an A/B test's overrides live on the test."
        emptyIcon={<SlidersHorizontal aria-hidden="true" />}
      />
    </ConditionOptionsContext.Provider>
  );
}

function OverrideHeader({ ctx }: { ctx: CardContext }) {
  const groups = groupsOf(ctx.doc);
  const rows = groups.reduce((n, g) => n + g.overrides.length, 0);
  return (
    <div className="card-head-lines">
      <div className="lo-head-line">
        <NameText doc={ctx.doc} />
        <span className="lo-meta">
          {groups.length} group{groups.length === 1 ? '' : 's'} · {rows} row{rows === 1 ? '' : 's'}
        </span>
        {groups.slice(0, 2).map((g, i) => (
          <span key={i} className="lo-cond">
            <Filter aria-hidden="true" />
            <ConditionSummary rootId={g.condition} />
          </span>
        ))}
      </div>
    </div>
  );
}

function OverrideBody({ ctx, family }: { ctx: CardContext; family: Document[] }) {
  const env = useWidgetEnv();
  const { catalog, workspace } = env;
  useWorkspaceVersion(workspace);
  const groups = groupsOf(ctx.doc);
  const setGroups = (next: OverrideGroupData[]) => ctx.edit('data.groups', next.map(toGroupJson));
  const removeGroup = (i: number) => {
    for (const id of groups[i]!.overrides) removeComponent(id);
    setGroups(groups.filter((_, j) => j !== i));
  };
  const removeComponent = (id: string) => {
    const node = catalog.resolve(id);
    workspace.remove('document', id, node ? baseOf(node as Document) : undefined, { label: 'override row', template: node?.template });
  };
  const effective = useMemo(() => effectivePatches(catalog, family.some((d) => d.id === ctx.doc.id) ? family : [...family, ctx.doc]), [catalog, family, ctx.doc]);
  const mine = effective.filter((e) => e.winner.override.id === ctx.doc.id || e.others.some((o) => o.override.id === ctx.doc.id));
  return (
    <div className="override-card lo-override-body">
      {groups.map((g, i) => (
        <GroupSection key={i} ctx={ctx} index={i} group={g} onChange={(next) => setGroups(groups.map((x, j) => (j === i ? next : x)))} onRemove={() => removeGroup(i)} onRemoveRow={removeComponent} />
      ))}
      {groups.length === 0 && <EmptyState icon={<Layers aria-hidden="true" />} title="No groups yet" body="A group is a condition and the rows it patches for the players who pass it." />}
      {ctx.canWrite && (
        <div className="lo-override-actions">
          <Button size="s" onClick={() => setGroups([...groups, { condition: null, overrides: [], comments: '', priority: 100 }])} icon={<Plus aria-hidden="true" />}>
            Add group
          </Button>
        </div>
      )}
      {mine.length > 0 && (
        <div className="pane lo-effective">
          <div className="pane-body">
            <div className="section-head">
              <h3>Effective values</h3>
              <span className="muted">What a player passing these groups sees</span>
            </div>
            <EffectivePreview effective={mine} self={ctx.doc.id} />
          </div>
        </div>
      )}
      <section className="lo-group" aria-label="Notes">
        <h4 className="lo-group-title">Notes</h4>
        <CommonTail ctx={ctx} />
      </section>
    </div>
  );
}

function GroupSection({ ctx, index, group, onChange, onRemove, onRemoveRow }: { ctx: CardContext; index: number; group: OverrideGroupData; onChange: (g: OverrideGroupData) => void; onRemove: () => void; onRemoveRow: (id: string) => void }) {
  const catalog = useContent();
  const conditionParam = useMemo<Parameter>(() => ({ name: `groups[${index}].condition`, displayName: `Group ${index + 1} condition`, type: 'document', template: 'SmartObjects.Conditions.Logic', of: null }) as unknown as Parameter, [index]);
  const [adding, setAdding] = useState<Document | null>(null);
  const [picking, setPicking] = useState(false);
  const env = useWidgetEnv();
  const addRows = (target: Document, leaves: OverridableLeaf[]) => {
    const ids = leaves.map((leaf) => stageRow(env, target, leaf));
    onChange({ ...group, overrides: [...group.overrides, ...ids] });
    setAdding(null);
  };
  return (
    <section className="pane lo-override-group" data-testid={`override-group-${index}`} aria-label={`Group ${index + 1}`}>
      <div className="pane-head">
        <h3>Group {index + 1}</h3>
        <label className="lo-inline-field">
          Priority
          <NumberInput size="s" kind="int" value={group.priority} disabled={!ctx.canWrite} aria-label={`Group ${index + 1} priority`} onCommit={(v) => onChange({ ...group, priority: Number(v ?? 100) })} />
        </label>
        <span className="lo-comment">
          <CommitTextInput size="s" value={group.comments} placeholder="Add a comment…" disabled={!ctx.canWrite} aria-label={`Group ${index + 1} comment`} onCommit={(v) => onChange({ ...group, comments: v })} />
        </span>
        <span className="spacer" />
        <span className="lo-cond">
          <Filter aria-hidden="true" />
          <ConditionSummary rootId={group.condition} />
        </span>
        {ctx.canWrite && (
          <IconButton label={`Remove group ${index + 1}`} onClick={onRemove}>
            <Trash2 aria-hidden="true" />
          </IconButton>
        )}
      </div>
      <div className="pane-body">
        <FieldWidget param={conditionParam} value={group.condition} owner={ctx.doc.id} disabled={!ctx.canWrite} onCommit={(v) => onChange({ ...group, condition: typeof v === 'string' ? v : null })} />
        <div className="override-rows lo-override-table" role="table" aria-label={`Group ${index + 1} rows`}>
          <div className="override-row override-head" role="row">
            <span role="columnheader">Document</span>
            <span role="columnheader">Parameter</span>
            <span role="columnheader">New value</span>
            <span role="columnheader">Default value</span>
            <span role="columnheader">
              <span className="visually-hidden">Actions</span>
            </span>
          </div>
          {group.overrides.map((id) => (
            <PatchRowView key={id} id={id} canWrite={ctx.canWrite} onReplace={(oldId, newId) => onChange({ ...group, overrides: group.overrides.map((x) => (x === oldId ? newId : x)) })} onRemove={() => { onRemoveRow(id); onChange({ ...group, overrides: group.overrides.filter((x) => x !== id) }); }} />
          ))}
          {group.overrides.length === 0 && (
            <div className="override-row" role="row">
              <span role="cell" className="lo-row-span lo-override-empty">
                No rows: the group patches nothing yet.
              </span>
            </div>
          )}
        </div>
        {ctx.canWrite && (
          <div className="lo-override-actions">
            <Button size="s" onClick={() => setPicking(true)} icon={<Plus aria-hidden="true" />}>
              Add row
            </Button>
          </div>
        )}
        {picking && (
          <ReferencePicker
            param={{ template: null, name: 'document', displayName: 'Document to patch', type: 'document' }}
            selected={[]}
            onClose={() => setPicking(false)}
            onPick={(ids) => {
              setPicking(false);
              if (ids[0]) void resolveDocument(catalog, ids[0]).then((doc) => doc && setAdding(doc));
            }}
          />
        )}
        {adding && <ParameterPickDialog doc={adding} title="Params to override" confirmLabel="Add rows" onClose={() => setAdding(null)} onPick={(leaves) => addRows(adding, leaves)} />}
      </div>
    </section>
  );
}

/** One row: the patch component; the parameter picker re-targets through `›` and swaps the component when the kind changes. */
function PatchRowView({ id, canWrite, onReplace, onRemove }: { id: string; canWrite: boolean; onReplace: (oldId: string, newId: string) => void; onRemove: () => void }) {
  const env = useWidgetEnv();
  const { catalog, workspace } = env;
  const node = catalog.resolve(id);
  const row = patchOf(node, id);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!row) {
    return (
      <div className="override-row" role="row">
        <span role="cell" className="lo-row-span" aria-label={`Loading row ${id.slice(0, 8)}`}>
          <span className="skeleton" />
        </span>
      </div>
    );
  }
  const target = row.document ? (catalog.resolve(row.document) as Document | undefined) : undefined;
  const targetTemplate = target ? catalog.templates.get(target.template) : undefined;
  const param = targetTemplate ? paramAt(catalog, targetTemplate, row.path) : undefined;
  const def = target && param ? formatValue(param, valueAt(target.data as Json, row.path) as Json | undefined, catalog.format) : '—';
  const edit = (field: string, value: Json) => workspace.edit('document', id, `data.${field}`, value, { base: node ? baseOf(node as Document) : undefined, label: 'override row', template: node?.template });
  const retarget = (doc: Document, leaf: OverridableLeaf) => {
    if (leaf.template === row.template) {
      edit('document', doc.id);
      edit('path', leaf.path);
      edit('value', (valueAt(doc.data as Json, leaf.path) ?? null) as Json);
    } else {
      // another kind: a new component replaces the old one in the same step (section 8.3)
      const newId = stageRow(env, doc, leaf);
      workspace.remove('document', id, node ? baseOf(node as Document) : undefined, { label: 'override row', template: node?.template });
      onReplace(id, newId);
    }
    setAnchor(null);
  };
  return (
    <div className="override-row" role="row" data-testid={`override-row-${id}`}>
      <span role="cell">{target ? catalog.label(target) : <span className="muted">(no document)</span>}</span>
      <span role="cell">
        <button type="button" className="link-button lo-param-button" disabled={!canWrite} onClick={(e) => setAnchor(e.currentTarget)} aria-label={`Parameter of row ${id.slice(0, 8)}`}>
          {param ? (param.displayName ?? param.name) : row.path || '(pick…)'} <ChevronDown className="icon-s" aria-hidden="true" />
        </button>
        {anchor && target && <ParameterMenu anchor={anchor} doc={target} onClose={() => setAnchor(null)} onPick={retarget} />}
      </span>
      <span role="cell">{param ? <FieldWidget param={{ ...param, name: 'value', displayName: `${param.displayName ?? param.name} new value` }} value={row.value} compact disabled={!canWrite} onCommit={(v) => edit('value', v)} /> : <span className="muted">—</span>}</span>
      <span role="cell" className="muted" title="The target's current value">
        {def}
      </span>
      <span role="cell">
        {canWrite && (
          <IconButton label={`Remove row ${id.slice(0, 8)}`} onClick={onRemove}>
            <X aria-hidden="true" />
          </IconButton>
        )}
      </span>
    </div>
  );
}

/** The parameter picker of a row: the target's leaves, and `›` into a referenced document (re-targets the row at it). */
function ParameterMenu({ anchor, doc, onClose, onPick }: { anchor: HTMLElement; doc: Document; onClose: () => void; onPick: (doc: Document, leaf: OverridableLeaf) => void }) {
  const catalog = useContent();
  const [stack, setStack] = useState<Document[]>([doc]);
  const current = stack[stack.length - 1]!;
  const template = catalog.templates.get(current.template);
  const leaves = template ? overridableLeaves(catalog, template) : [];
  const refs = template ? template.effectiveParameters.filter((p) => p.type === 'document' && typeof (current.data as Record<string, Json>)[p.name] === 'string') : [];
  return (
    <Popover anchor={anchor} open onClose={onClose} label="Parameter" className="param-menu lo-param-menu">
      <div className="lo-param-path">{stack.map((d) => catalog.label(d)).join(' › ')}</div>
      {stack.length > 1 && (
        <button type="button" className="picker-entry" onClick={() => setStack(stack.slice(0, -1))}>
          <ChevronLeft className="icon-s" aria-hidden="true" /> Back
        </button>
      )}
      {leaves.map((l) => (
        <button key={l.path} type="button" className="picker-entry" onClick={() => onPick(current, l)}>
          {l.label} <span className="muted small">{l.param.type}</span>
        </button>
      ))}
      {refs.map((p) => {
        const refId = (current.data as Record<string, Json>)[p.name] as string;
        const ref = catalog.resolve(refId) as Document | undefined;
        // the entry is offered as soon as the reference is set; the label arrives when the resolver has the document
        return (
          <button key={`ref-${p.name}`} type="button" className="picker-entry" disabled={!ref} onClick={() => ref && setStack([...stack, ref])} title="Patch a field of the referenced document (the row is re-targeted at it)">
            {p.displayName ?? p.name} › {ref ? catalog.label(ref) : `${refId.slice(0, 8)}…`}
          </button>
        );
      })}
      {leaves.length === 0 && refs.length === 0 && <p className="lo-note">Nothing patchable here.</p>}
    </Popover>
  );
}

/** "What a player passing these groups sees": the winner per (document, path) with the conflicts flagged (8.3). */
export function EffectivePreview({ effective, self }: { effective: Effective[]; self: string }) {
  const catalog = useContent();
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  const byDoc = new Map<string, Effective[]>();
  for (const e of effective) byDoc.set(e.document, [...(byDoc.get(e.document) ?? []), e]);
  const text = (row: PatchRow): ReactNode => {
    const target = row.document ? (catalog.resolve(row.document) as Document | undefined) : undefined;
    const param = target ? paramAt(catalog, catalog.templates.get(target.template), row.path) : undefined;
    return param ? formatValue(param, row.value, catalog.format) : JSON.stringify(row.value ?? null);
  };
  return (
    <div className="effective-preview" data-testid="effective-preview" aria-label="Effective values: what a player passing these groups sees">
      {Array.from(byDoc.entries()).map(([docId, list]) => {
        const target = catalog.resolve(docId) as Document | undefined;
        return (
          <div key={docId} className="effective-doc">
            <div className="effective-doc-name">{target ? catalog.label(target) : docId.slice(0, 8)}</div>
            {list.map((e) => {
              const param = target ? paramAt(catalog, catalog.templates.get(target.template), e.path) : undefined;
              const def = target && param ? formatValue(param, valueAt(target.data as Json, e.path) as Json | undefined, catalog.format) : '—';
              const conflict = e.others.length > 0;
              const from = (c: typeof e.winner) => (c.override.id === self ? `group ${c.groupIndex + 1}` : <Link to={`${routes.branch(org, game.slug, branch, 'liveops/overrides')}?doc=${encodeURIComponent(c.override.id)}`}>{catalog.label(c.override)} › group {c.groupIndex + 1}</Link>);
              return (
                <div key={e.path} className={`effective-row${conflict ? ' conflict' : ''}`}>
                  <span className="effective-field">{param ? (param.displayName ?? param.name) : e.path}:</span> <span className="muted">{def}</span> → <strong>{text(e.winner.row)}</strong> <span className="muted small">(from {from(e.winner)}, priority {e.winner.priority})</span>
                  {conflict && (
                    <span className="row wrap">
                      <Badge tone="warning" title="Two groups patch this field; the highest priority wins, ties go to the lowest legacy id">
                        conflict
                      </Badge>
                      {e.others.map((o, i) => (
                        <span key={i} className="muted small">
                          also {text(o.row)} from {from(o)} (priority {o.priority})
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/** A new empty override document (the + Create menu stages a template default; this is the wizard-less path). */
export function newOverride(env: ReturnType<typeof useWidgetEnv>): string {
  const t = env.catalog.templates.get(OVERRIDE_INFO);
  if (!t) throw new Error('The branch has no SmartObjects.OverrideInfo template.');
  return createDocumentDraft(env.workspace, t, { name: 'New override', flag: 0, groups: [toGroupJson({ condition: null, overrides: [], comments: '', priority: 100 })] });
}
